import type { StateCreator } from 'zustand';
import { getInitialStateFromStorage } from 'components/common/dataGrid/utils';
import { DataGridInstanceName } from 'services/storage/keys';
import { ROOT_DIR_ID } from 'shared/constants/directory';
import { areDirectoryIdsEqual } from 'shared/domain/directory/directoryIds';
import { areObjectIdsEqual } from 'helpers/idComparison';
import { isNonEmptyArray } from 'shared/utils/array';
import type { DocumentationStoreSlices } from '../types';
import { resolveDocumentationModuleLoad } from './model';
import type { DocumentationUrlTarget, ModuleLifecycleSlice } from './types';

export * from './types';

type ModuleLifecycleSliceCreator = StateCreator<
  DocumentationStoreSlices,
  [],
  [],
  ModuleLifecycleSlice
>;

export const createModuleLifecycleSlice: ModuleLifecycleSliceCreator = (set, get) => {
  // Shared by all open module instances; not rendered, so kept out of state.
  let openInstances = 0;
  let loadedProjectId: string | undefined;

  const loadDirectoryContent = async (refreshInBackground: boolean): Promise<void> => {
    const { filterModel, sortModel } = getInitialStateFromStorage(
      DataGridInstanceName.documentation
    );
    get().setFilterModel(filterModel);
    get().setSortModel(sortModel);

    // Independent requests - load them in parallel.
    await Promise.all([
      get().fetchAllDirectories(),
      get().fetchDirectoryContent(get().currentDirectory, {
        suppressLoadingIndicators: refreshInBackground,
      }),
    ]);
  };

  const loadProject = (
    projectId: string,
    { directoryId, fileId }: DocumentationUrlTarget,
    isSameProject: boolean
  ): void => {
    const targetDirectory = directoryId ?? ROOT_DIR_ID;
    const previousDirectory = get().currentDirectory;
    // Returning to the same project and directory: keep its rows on screen and
    // refresh them in the background (no loading overlay).
    const refreshInBackground =
      isSameProject &&
      areDirectoryIdsEqual(previousDirectory, targetDirectory) &&
      isNonEmptyArray(get().filteredRows);

    get().initializeSearchPhrase(projectId);
    get().setCurrentDirectory(targetDirectory);

    // Returning to the same directory keeps its cached rows on screen while the
    // fresh fetch runs; another directory must not show stale rows meanwhile.
    if (!areDirectoryIdsEqual(previousDirectory, targetDirectory)) {
      get().clearCurrentDirectoryContent();
    }

    // One `findRoots` request fills both the breadcrumbs and the directory path.
    void get().fetchBreadcrumbs(targetDirectory);

    // On /documentation/file/:fileId the target directory is not known yet -
    // do not preload root content (it would race the URL file navigation).
    if (fileId && !directoryId) {
      void get().fetchAllDirectories();
      return;
    }

    void loadDirectoryContent(refreshInBackground);
  };

  return {
    projectDataGeneration: 0,

    resetStore: (): void => {
      set({ projectDataGeneration: get().projectDataGeneration + 1 });
      get().cancelPendingChannelRefreshes();
      get().resetDataState();
      get().resetFiltersState();
      get().resetNavigationState();
      get().resetGalleryState();
      get().resetOperationsState();
      get().clearUrlCaches();
      get().resetDownloadState();
      get().resetUploadState();
      get().resetOptimisticState();
    },

    openDocumentationModule: (projectId, urlTarget): (() => void) => {
      const plan = resolveDocumentationModuleLoad({
        projectId,
        loadedProjectId,
        isFirstOpenInstance: openInstances === 0,
      });
      openInstances += 1;
      const isSameProject = areObjectIdsEqual(loadedProjectId, projectId);

      if (plan.resetStore) {
        get().resetStore();
      }
      loadedProjectId = projectId || undefined;
      if (plan.loadProject && projectId) {
        loadProject(projectId, urlTarget, isSameProject);
      }

      let closed = false;
      return () => {
        if (closed) {
          return;
        }
        closed = true;
        openInstances = Math.max(0, openInstances - 1);
      };
    },
  };
};
